/**
 * EVO — satr ustida ANIQ o'nlik arifmetika.
 *
 * ============================================================================
 * NIMA UCHUN BU FAYL UMUMAN BOR
 * ============================================================================
 *
 * `format.ts` da qat'iy qoida yozilgan: pul API'dan `string` bo'lib keladi va
 * `string` bo'lib qoladi, arifmetika esa BRAUZERDA QILINMAYDI — u serverda
 * `@evo/core` (Decimal.js) orqali bajariladi.
 *
 * Hisobot ekranlarida bu qoidaning bitta ONGLI istisnosi bor: JAMI SATRI.
 * Sabab amaliy — `/api/reports/*` javoblarida jami maydoni YO'Q (reports.service.ts
 * har hisobotda `{ meta, rows }` qaytaradi, `total` emas), lekin rahbar
 * jadvalning oxirgi qatorini birinchi bo'lib qidiradi. Ikki yo'l bor edi:
 *
 *   1. `rows.reduce((s, r) => s + parseFloat(r.total), 0)` — bu TAQIQLANGAN.
 *      `parseFloat` IEEE 754 float beradi va 5 000 satrli ombor qiymatida
 *      buxgalteriya bilan tiyinlab farq chiqadi (GAP_ANALYSIS §C4).
 *   2. Butun sonlar (BigInt) ustida aniq qo'shish — SHU FAYL.
 *
 * BigInt tanlandi, chunki u ixtiyoriy uzunlikdagi BUTUN sonni ANIQ saqlaydi.
 * `"14977.4100"` → `149774100` (miqyos 4) ko'rinishiga keltiriladi, qo'shiladi
 * va yana satrga qaytariladi. Hech qayerda `Number` ishlatilmaydi.
 *
 * ============================================================================
 * KO'PAYTIRISH VA FOIZ — KEYIN QO'SHILDI, VA NIMA UCHUN
 * ============================================================================
 *
 * Avval bu faylda faqat qo'shish bor edi: «bo'lish, ko'paytirish va foiz
 * yaxlitlash siyosatini talab qiladi, u esa SERVERDA turishi kerak».
 *
 * Buyurtma KIRITISH ekrani (`/sales/orders/new`) bu qoidaga bitta yangi
 * talab qo'shdi: satr HALI SAQLANMAGAN paytda ham sotuvchi «bu satr qancha
 * turadi» va «jami qancha» ni ko'rishi kerak. Server bu raqamni bera olmaydi —
 * hujjat hali yo'q. Sotuvchi esa mijoz bilan telefonda gaplashib turadi va
 * summani AYNAN shu daqiqada aytadi.
 *
 * Shuning uchun quyidagilar qo'shildi, lekin QAT'IY chegara bilan:
 *
 *   • `mulDecimals`, `subDecimals` — YAXLITLASHSIZ. Ko'paytmaning miqyosi
 *     ikkala qiymat miqyoslari YIG'INDISI bo'ladi va bironta raqam
 *     tashlanmaydi. Ya'ni bu yerda «siyosat» degan narsa umuman yo'q.
 *   • `applyDiscountPct` — 100 ga bo'lish. Bu ham ANIQ: o'nlik sanoqda 100 ga
 *     bo'lish — vergulni ikki xona surish, ya'ni miqyosga +2.
 *   • `discountPctOf` — haqiqiy bo'lish bor bo'lgan BIRINCHI joy. U half-up
 *     bilan 2 xonagacha yaxlitlaydi va FAQAT KO'RSATISH uchun: chegirma
 *     limitini tekshirish serverda (`assertDiscountAllowed`), hujjatga
 *     yoziladigan foiz ham serverdan qaytadi.
 *   • `divDecimalByInt` — IKKINCHI va oxirgisi. Usta dasturidagi BALL
 *     QIYMATI (`cost_base ÷ points_cost`) uchun: server bu raqamni har
 *     sovg'a bo'yicha bermaydi, faqat katalog o'rtachasini beradi. Batafsil
 *     sabab funksiyaning o'z izohida.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ EKRANDAGI SUMMA — OLDINDAN KO'RISH, HUJJAT EMAS.                     │
 * │ Buyurtma saqlangandan keyin ekran SERVER qaytargan `totalAmount` ni   │
 * │ ko'rsatadi, brauzerda hisoblanganini emas. Ikkalasi farq qilsa,       │
 * │ haqiqat — serverniki.                                                 │
 * └──────────────────────────────────────────────────────────────────────┘
 */

/** `+`/`-`, raqamlar va bitta nuqta. Eksponent (`1e5`) qabul qilinmaydi — Postgres uni yubormaydi. */
const DECIMAL_PATTERN = /^[+-]?\d+(\.\d+)?$/;

/** Miqyoslangan ko'rinish: `"14.12"` → `{ digits: 1412n, scale: 2 }`. */
interface Scaled {
  readonly digits: bigint;
  readonly scale: number;
}

export function isDecimalString(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_PATTERN.test(value.trim());
}

/** Yaroqsiz qiymatda `null` — chaqiruvchi «buzuq ma'lumot» ni ko'ra oladi. */
function parse(raw: string | null | undefined): Scaled | null {
  if (raw === null || raw === undefined) return null;

  const trimmed = raw.trim();
  if (!DECIMAL_PATTERN.test(trimmed)) return null;

  const negative = trimmed.startsWith('-');
  const body = trimmed.replace(/^[+-]/, '');
  const dot = body.indexOf('.');
  const int = dot === -1 ? body : body.slice(0, dot);
  const frac = dot === -1 ? '' : body.slice(dot + 1);

  // `BigInt('007')` → `7n`; nol prefiksi muammo emas.
  const digits = BigInt(`${int}${frac}`);
  return { digits: negative ? -digits : digits, scale: frac.length };
}

/** Miqyoslangan sonni yana satrga qaytaradi — kasr xonalari SAQLANADI. */
function render({ digits, scale }: Scaled): string {
  const negative = digits < 0n;
  const abs = (negative ? -digits : digits).toString();

  if (scale === 0) return `${negative ? '-' : ''}${abs}`;

  // `padStart(scale + 1)` — `0.05` uchun butun qismda kamida bitta raqam qoladi.
  const padded = abs.padStart(scale + 1, '0');
  const int = padded.slice(0, padded.length - scale);
  const frac = padded.slice(padded.length - scale);
  return `${negative ? '-' : ''}${int}.${frac}`;
}

/** Ikki qiymatni umumiy miqyosga keltiradi: `1.5` va `2.25` → `150` va `225` (miqyos 2). */
function align(a: Scaled, b: Scaled): { readonly a: bigint; readonly b: bigint; readonly scale: number } {
  const scale = Math.max(a.scale, b.scale);
  return {
    a: a.digits * 10n ** BigInt(scale - a.scale),
    b: b.digits * 10n ** BigInt(scale - b.scale),
    scale,
  };
}

/**
 * Qiymatlar yig'indisi.
 *
 * Qoidalar:
 *   • `null`/`undefined` — NOL deb olinadi. Bu «ustun ruxsat bo'yicha
 *     kelmadi» yoki «SQL `NULL` qaytardi» degani, ikkalasi ham jami uchun nol.
 *   • Bor, lekin YAROQSIZ qiymat (`"abc"`) — butun jami `null` bo'ladi.
 *     Sabab: buzuq satrni jimgina tashlab yuborish NOTO'G'RI jami beradi,
 *     va noto'g'ri jami — jami yo'qligidan yomonroq.
 *   • Bo'sh ro'yxat — `"0"`.
 */
export function sumDecimals(values: Iterable<string | null | undefined>): string | null {
  let acc: Scaled = { digits: 0n, scale: 0 };

  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;

    const parsed = parse(value);
    if (parsed === null) return null;

    const { a, b, scale } = align(acc, parsed);
    acc = { digits: a + b, scale };
  }

  return render(acc);
}

/**
 * `sumDecimals` — lekin «MA'LUMOT YO'Q» ni saqlaydi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN ALOHIDA FUNKSIYA KERAK                                        ║
 * ║                                                                          ║
 * ║ `sumDecimals([])` "0" qaytaradi va bu TO'G'RI: bo'sh yig'indi nolga      ║
 * ║ teng. Ekranlarda esa naqsh `sumDecimals(data?.rows ?? [])` edi va aynan  ║
 * ║ o'sha `?? []` xato bilan qaytgan so'rovni bo'sh ro'yxatga aylantirardi   ║
 * ║ — ya'ni «hisobot yuklanmadi» va «hech nima yo'q» bir xil `$0.00` bo'lib  ║
 * ║ chiqardi.                                                               ║
 * ║                                                                          ║
 * ║ Jonli tekshirilgan: `/reports/ap-aging` bloklanganda «Ta'minotchi        ║
 * ║ qarzlari» ekrani qizil xato banneri OSTIDA beshta `$0.00` plitkani      ║
 * ║ chizardi, «muddati o'tgan» va «osilgan avans» esa YASHIL edi — ya'ni    ║
 * ║ ekran hech narsa bilmay turib «qarz yo'q» deb tasdiqlardi.              ║
 * ║                                                                          ║
 * ║ Bu qoida loyihada allaqachon yozilgan (`isZeroDecimal`: «nol» va        ║
 * ║ «ma'lumot yo'q» bir narsa emas), faqat YIG'INDIGA tatbiq qilinmagandi.  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Chaqiruv naqshi: `sumDecimalsOrNull(rows?.map((r) => r.amount))` — `rows`
 * `undefined` bo'lsa `?.` butun ifodani `undefined` qiladi va natija `null`.
 */
export function sumDecimalsOrNull(
  values: Iterable<string | null | undefined> | undefined,
): string | null {
  return values === undefined ? null : sumDecimals(values);
}

/** Ikki qiymatni qo'shadi. Bittasi yaroqsiz bo'lsa — `null`. */
export function addDecimals(a: string | null | undefined, b: string | null | undefined): string | null {
  return sumDecimals([a, b]);
}

/**
 * Solishtirish — jadvalni saralash uchun.
 *
 * `"1000"` va `"999.5"` ni SATR sifatida solishtirish `"1000" < "999.5"`
 * beradi (leksikografik), ya'ni eng qimmat tovar ro'yxat o'rtasida qolib
 * ketadi. Shuning uchun bu yerda ham BigInt.
 *
 * Yaroqsiz qiymat oxiriga tushadi: bo'sh katak jadval boshini egallamaydi.
 */
export function compareDecimals(a: string | null | undefined, b: string | null | undefined): number {
  const left = parse(a);
  const right = parse(b);

  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;

  const aligned = align(left, right);
  if (aligned.a === aligned.b) return 0;
  return aligned.a < aligned.b ? -1 : 1;
}

/** Qiymat noldan katta ekanini tekshiradi — «qarzi bor», «kechikkan» kabi holatlar uchun. */
export function isPositiveDecimal(value: string | null | undefined): boolean {
  const parsed = parse(value);
  return parsed !== null && parsed.digits > 0n;
}

/** Qiymat aniq nolmi. Bo'sh/yaroqsiz — `false`: «nol» va «ma'lumot yo'q» bir narsa emas. */
export function isZeroDecimal(value: string | null | undefined): boolean {
  const parsed = parse(value);
  return parsed !== null && parsed.digits === 0n;
}

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ MANFIY SON QACHON QIZIL BO'LADI — YAGONA QOIDA VA U SHU YERDA TURADI     ║
 * ║                                                                          ║
 * ║ ISHORA XOM QIYMATDAN OLINADI, FORMATLANGAN MATNDAN EMAS. `format.ts`     ║
 * ║ ekranga HAQIQIY minus (U+2212) chiqaradi, klaviaturadagi defis-minusni   ║
 * ║ emas. Ya'ni `formatMoney(...).startsWith('-')` DOIM `false` qaytaradi:   ║
 * ║ tekshiruv yiqilmaydi, xato bermaydi — shunchaki rang yo'qoladi va buni   ║
 * ║ hech kim sezmaydi. Shu funksiya `parse()` orqali xom satrni o'qiydi.     ║
 * ║                                                                          ║
 * ║ QIZIL FAQAT MANFIYLIKNING O'ZI NOSOZLIK BO'LGAN USTUNDA:                 ║
 * ║                                                                          ║
 * ║   · ombor qoldig'i va erkin qoldiq (`inventory/stock.tsx`,               ║
 * ║     `reports/stock.tsx`) — manfiy bo'lsa tovar bor-yo'g'idan ko'p band   ║
 * ║     qilingan yoki chiqim qilingan, ya'ni ombor raqami YOLG'ON;           ║
 * ║   · harakatdan keyingi qoldiq (`inventory/movements.tsx`) — o'sha        ║
 * ║     nosozlikning daftardagi izi, uni AYNAN qaysi hujjat tug'dirgani      ║
 * ║     shu satrda ko'rinadi;                                                ║
 * ║   · komissiya sof summasi (`hr/commission-runs.tsx`) — ushlanmalar       ║
 * ║     jamg'armadan oshgan, ya'ni xodimga to'lanmaydi, undan UNDIRILADI.    ║
 * ║                                                                          ║
 * ║ Uchalasida ham javob bitta: kimdir bugun borib tekshirishi kerak.        ║
 * ║                                                                          ║
 * ║ QIZIL BO'LMAYDI — manfiylik ODATIY VA KUTILGAN joyda: storno to'lov      ║
 * ║ (`finance/payments.tsx`), chegirma, vozvrat, tannarx tuzatishi, kurs     ║
 * ║ farqi, buxgalteriya kreditlari. Ular kuniga o'nlab uchraydi va           ║
 * ║ bo'yalsa ekran qizil shovqinga aylanadi — o'shanda ROSTAN nosoz          ║
 * ║ bo'lgan bitta qoldiq ko'zga tashlanmay qoladi. Rang — ogohlantirish,     ║
 * ║ bezak emas: u qanchalik kam ishlatilsa, shunchalik ko'p narsa aytadi.    ║
 * ║                                                                          ║
 * ║ Yangi ustunni bo'yashdan oldin savol bitta: «bu raqam manfiy bo'lsa,     ║
 * ║ kimdir BUGUN nimadir qilishi kerakmi?». Javob «yo'q» bo'lsa — u xuddi    ║
 * ║ ijobiy qiymatdek neytral qoladi.                                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export function isNegativeDecimal(value: string | null | undefined): boolean {
  const parsed = parse(value);
  return parsed !== null && parsed.digits < 0n;
}

/** `a − b`. Yaxlitlash YO'Q. Bittasi yaroqsiz bo'lsa — `null`. */
export function subDecimals(a: string | null | undefined, b: string | null | undefined): string | null {
  const left = parse(a);
  const right = parse(b);
  if (left === null || right === null) return null;

  const aligned = align(left, right);
  return render({ digits: aligned.a - aligned.b, scale: aligned.scale });
}

/**
 * `a × b` — ANIQ, yaxlitlashsiz.
 *
 * Miqyos = miqyoslar yig'indisi: `"1.50" × "3.000"` → `"4.50000"` (2+3=5 xona).
 * Ortiqcha nollar ATAYLAB qoldiriladi — kesish yaxlitlash siyosati demakdir,
 * u esa serverning ishi. Ekranda `formatMoney`/`formatQty` baribir qisqartadi.
 */
export function mulDecimals(a: string | null | undefined, b: string | null | undefined): string | null {
  const left = parse(a);
  const right = parse(b);
  if (left === null || right === null) return null;

  return render({ digits: left.digits * right.digits, scale: left.scale + right.scale });
}

/**
 * Chegirma qo'llangan narx: `price × (100 − pct) / 100`. ANIQ.
 *
 * 100 ga bo'lish o'nlik sanoqda vergulni ikki xona surish demak, ya'ni
 * yaxlitlash umuman kerak emas — natija miqyosi ikkitaga oshadi, xolos.
 *
 * Server AYNAN shu qiymatni 6 xonagacha yaxlitlab yozadi (`subPct(..., 6)`),
 * shuning uchun ekrandagi va hujjatdagi narx oxirgi xonada farq qilishi
 * mumkin — bu kutilgan holat, hujjatdagi qiymat haqiqiy.
 */
export function applyDiscountPct(
  price: string | null | undefined,
  pct: string | null | undefined,
): string | null {
  const remaining = subDecimals('100', pct ?? '0');
  if (remaining === null) return null;

  const product = mulDecimals(price, remaining);
  if (product === null) return null;

  const parsed = parse(product);
  if (parsed === null) return null;

  // ÷100 → miqyos +2. Raqamlarning o'zi o'zgarmaydi, ya'ni hech narsa yo'qolmaydi.
  return render({ digits: parsed.digits, scale: parsed.scale + 2 });
}

/**
 * `value ÷ divisor` — BUTUN songa bo'lish, `scale` xonagacha half-up.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ BU FAYLDAGI IKKINCHI VA OXIRGI HAQIQIY BO'LISH.                      │
 * │                                                                      │
 * │ Nima uchun kerak: usta dasturida BALL QIYMATI hosila raqam —          │
 * │ `rewards.cost_base ÷ rewards.points_cost` (sovg'a tannarxi ÷ uning    │
 * │ ball narxi). Serverda u faqat KATALOG BO'YICHA O'RTACHA sifatida      │
 * │ chiqadi (`GET /loyalty/liability` → `pointValueUsd`), HAR SOVG'A      │
 * │ bo'yicha esa umuman qaytarilmaydi. Sovg'a katalogi ekranining butun   │
 * │ ma'nosi aynan shu taqqoslashda: bitta sovg'a boshqalardan foydali     │
 * │ bo'lsa, usta buni hisoblab chiqadi va katalogning qolgani             │
 * │ ishlatilmaydi (LOYALTY_SPEC §7 «Nisbat ataylab izchil»).              │
 * │                                                                      │
 * │ Bo'luvchi ATAYLAB `number`, `string` emas: ball — BUTUN son           │
 * │ (`points_cost integer`), ya'ni bu yerda o'nlik bo'luvchi umuman       │
 * │ bo'lishi mumkin emas va uni qabul qilish noto'g'ri ishlatishga yo'l   │
 * │ ochardi.                                                             │
 * │                                                                      │
 * │ Yaxlitlash — half-up, `scale` xonagacha. Bu YAXLITLASH NUQTASI va u  │
 * │ bitta: qolgan hamma joyda ball qiymati SHU funksiyadan chiqqan satr   │
 * │ bo'lib yuradi (`lib/loyalty.ts` → `derivedPointValueUsd`).            │
 * └──────────────────────────────────────────────────────────────────────┘
 */
export function divDecimalByInt(
  value: string | null | undefined,
  divisor: number,
  scale: number,
): string | null {
  const parsed = parse(value);
  if (parsed === null) return null;
  // Nolga va kasr bo'luvchiga bo'linmaydi — natija `null`, «0» EMAS: nol
  // «qiymati yo'q» degan xato xulosa beradi (server ham shu yerda `null` beradi).
  if (!Number.isInteger(divisor) || divisor <= 0 || scale < 0) return null;

  /*
   * Kasrni butunga aylantiramiz: `value` ni `scale` xonaga ko'chirib
   * bo'luvchiga bo'lamiz. Ko'chirish yo'nalishi `scale` va qiymat
   * miqyosining farqiga bog'liq — ikkalasi ham BigInt ustida, ya'ni
   * bironta raqam yo'qolmaydi.
   */
  const shift = scale - parsed.scale;
  let numerator = parsed.digits;
  let denominator = BigInt(divisor);
  if (shift >= 0) {
    numerator *= 10n ** BigInt(shift);
  } else {
    denominator *= 10n ** BigInt(-shift);
  }

  // Half-up: `floor(|n| / d + 1/2)`. Ikkala son musbat bo'lgani uchun
  // BigInt bo'lishi (nolga qarab kesish) aynan `floor` bo'lib ishlaydi.
  const negative = numerator < 0n;
  const abs = negative ? -numerator : numerator;
  const quotient = (abs * 2n + denominator) / (denominator * 2n);

  return render({ digits: negative ? -quotient : quotient, scale });
}

/**
 * Nazarda tutilgan chegirma foizi: `(listPrice − unitPrice) / listPrice × 100`.
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ FAQAT KO'RSATISH UCHUN.                                              │
 * │                                                                      │
 * │ Sotuvchi qo'lda narx kiritganda («$18 ga beraman») u aslida chegirma │
 * │ berayotganini KO'RISHI kerak — aks holda limit ma'nosini yo'qotadi   │
 * │ (`sales-orders.service.ts`: qo'lda narx ham AYNAN shu formula bilan  │
 * │ foizga aylantiriladi va limitdan o'tkaziladi).                       │
 * │                                                                      │
 * │ Lekin QAROR bu yerda qabul qilinmaydi: limitning o'zi (`discount_    │
 * │ rules.max_discount_pct`) hech qaysi endpointda qaytarilmaydi va      │
 * │ bloklashni faqat server biladi. Brauzer «10% dan oshdi» deb o'zicha  │
 * │ to'sib qo'ysa, u serverdagi haqiqiy limitdan farq qilib qoladi.      │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Yaxlitlash — half-up, 2 xona (`numeric(5,2)` bilan bir xil).
 * Narx ro'yxatdan YUQORI bo'lsa (ustama) — `"0.00"`, chunki bu chegirma emas.
 */
export function discountPctOf(
  listPrice: string | null | undefined,
  unitPrice: string | null | undefined,
): string | null {
  const list = parse(listPrice);
  const unit = parse(unitPrice);
  if (list === null || unit === null || list.digits <= 0n) return null;

  const aligned = align(list, unit);
  const diff = aligned.a - aligned.b;
  if (diff <= 0n) return '0.00';

  // `× 10000` — 100 (foiz) × 100 (ikkita kasr xona). Bo'luvchi musbat.
  const numerator = diff * 10000n;
  const denominator = aligned.a;

  // Half-up: `floor((2n + d) / 2d)`. Ikkala son ham musbat, shuning uchun
  // BigInt bo'lishi (nolga qarab kesish) aynan `floor` bo'lib ishlaydi.
  const scaledPct = (numerator * 2n + denominator) / (denominator * 2n);
  return render({ digits: scaledPct, scale: 2 });
}
